import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu, X, User, LogOut, CreditCard, Wallet, Bookmark, Lightbulb, Settings, Shield, LayoutDashboard, History, UserPlus, Loader2, Trophy, Bell } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import AuthModal from './AuthModal';
import NotificationSettingsDialog from './notifications/NotificationSettingsDialog';
import { trackFunnelEvent } from '../services/funnelService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Header = ({ onSignUpClick, onSubscribeClick }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showPricingDropdown, setShowPricingDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalView, setAuthModalView] = useState('login');
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const searchRef = useRef(null);
  const { user, isAuthenticated, isSubscribed, loginWithGoogle, logout, loading } = useAuth();

  const openAuthModal = (view = 'login') => {
    setAuthModalView(view);
    setAuthModalOpen(true);
  };

  // Search functionality
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchTimeout = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        setSearching(true);
        try {
          const response = await fetch(`${API_URL}/api/search?q=${encodeURIComponent(searchQuery)}&limit=10`);
          if (response.ok) {
            const data = await response.json();
            setSearchResults(data);
            setShowSearchResults(true);
          }
        } catch (error) {
          console.error('Search error:', error);
        } finally {
          setSearching(false);
        }
      } else {
        setSearchResults(null);
        setShowSearchResults(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [searchQuery]);

  const handleSearchResultClick = (result, type) => {
    setShowSearchResults(false);
    setSearchQuery('');
    if (type === 'market') {
      navigate(`/?market=${result.id}`);
    } else if (type === 'idea') {
      navigate(`/ideas?id=${result.id}`);
    } else if (type === 'category') {
      navigate(`/?category=${result.id}`);
    }
  };

  const handleSubscribe = async (tier = 'monthly') => {
    if (!isAuthenticated) {
      loginWithGoogle();
      return;
    }
    
    try {
      const { checkout_url } = await apiService.createCheckoutSession(tier);
      window.location.href = checkout_url;
    } catch (error) {
      console.error('Failed to create checkout:', error);
    }
    setShowPricingDropdown(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-[#050810]/95 backdrop-blur-md border-b border-[#1e2a45] shadow-[0_4px_20px_rgba(0,0,0,0.3)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" aria-label="amppredict home" data-testid="header-logo-link">
            <img
              src="/brand/amppredict-logo.png"
              alt="amppredict"
              className="h-12 w-auto select-none"
              draggable={false}
              data-testid="header-logo-img"
            />
            <span className="text-xs font-medium text-[#F97316] bg-[#F97316]/10 px-2 py-0.5 rounded-full border border-[#F97316]/20">Mock Predictions</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {isAuthenticated ? (
              <Link
                to="/ideas"
                onClick={() => trackFunnelEvent('banner_click', { source: 'nav_markets_creation', userId: user?.user_id })}
                data-testid="nav-markets-creation"
                className="text-sm font-extrabold uppercase tracking-wide text-white bg-gradient-to-b from-[#3B82F6] to-[#1D4ED8] hover:from-[#2563EB] hover:to-[#1E40AF] px-3 py-1.5 rounded-full shadow-[0_3px_0_0_#1E40AF] hover:shadow-[0_1px_0_0_#1E40AF] hover:translate-y-[2px] active:translate-y-[3px] active:shadow-none transition-all duration-100"
              >
                MARKETS CREATION
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => {
                  trackFunnelEvent('banner_click', { source: 'nav_markets_creation' });
                  trackFunnelEvent('auth_shown', { source: 'nav_markets_creation' });
                  openAuthModal('login');
                }}
                data-testid="nav-markets-creation"
                className="text-sm font-extrabold uppercase tracking-wide text-white bg-gradient-to-b from-[#3B82F6] to-[#1D4ED8] hover:from-[#2563EB] hover:to-[#1E40AF] px-3 py-1.5 rounded-full shadow-[0_3px_0_0_#1E40AF] hover:shadow-[0_1px_0_0_#1E40AF] hover:translate-y-[2px] active:translate-y-[3px] active:shadow-none transition-all duration-100"
              >
                MARKETS CREATION
              </button>
            )}
            <Link to="/" className="text-sm font-medium text-gray-300 hover:text-[#3B82F6] transition-colors">
              Markets
            </Link>
            <Link to="/?category=right_now" className="text-sm font-medium text-[#F97316] hover:text-[#3B82F6] transition-colors">
              Right Now
            </Link>
            <Link to="/scores" className="text-sm font-medium text-gray-300 hover:text-[#3B82F6] transition-colors">
              Scores
            </Link>
            <Link to="/leaderboard" className="text-sm font-medium text-gray-300 hover:text-[#3B82F6] transition-colors flex items-center gap-1">
              <Trophy className="h-3.5 w-3.5" />
              Leaderboard
            </Link>
            <Link to="/api" className="text-sm font-medium text-gray-300 hover:text-[#3B82F6] transition-colors">
              API
            </Link>
          </nav>

          {/* Search Bar */}
          <div className="hidden md:flex flex-1 max-w-md mx-8" ref={searchRef}>
            <div className={`relative w-full transition-all duration-200 ${
              searchFocused ? 'ring-2 ring-[#3B82F6] ring-opacity-50' : ''
            } rounded-full`}>
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                type="text"
                placeholder="Search markets, ideas, categories..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="header-search-input"
                className="w-full pl-10 pr-4 py-2 bg-[#131b2e] border-[#1e2a45] rounded-full text-sm text-gray-200 placeholder-gray-500 focus:bg-[#1a2438] focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                onFocus={() => { setSearchFocused(true); if (searchResults) setShowSearchResults(true); }}
                onBlur={() => setSearchFocused(false)}
              />
              {searching && (
                <Loader2 className="absolute right-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#3B82F6] animate-spin" />
              )}
              
              {/* Search Results Dropdown */}
              {showSearchResults && searchResults && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-[#131b2e] border border-[#1e2a45] rounded-xl shadow-2xl overflow-hidden z-50 max-h-96 overflow-y-auto">
                  {searchResults.total_count === 0 ? (
                    <div className="p-4 text-center text-gray-400 text-sm">
                      No results found for "{searchQuery}"
                    </div>
                  ) : (
                    <>
                      {/* Markets */}
                      {searchResults.markets?.length > 0 && (
                        <div className="p-2">
                          <h4 className="text-xs font-semibold text-gray-500 px-2 mb-1">MARKETS</h4>
                          {searchResults.markets.slice(0, 5).map(market => (
                            <button
                              key={market.id}
                              onClick={() => handleSearchResultClick(market, 'market')}
                              className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#1e2a45] transition-colors"
                            >
                              <p className="text-sm text-white truncate">{market.title}</p>
                              <p className="text-xs text-gray-500">{market.category}</p>
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {/* Ideas */}
                      {searchResults.ideas?.length > 0 && (
                        <div className="p-2 border-t border-[#1e2a45]">
                          <h4 className="text-xs font-semibold text-gray-500 px-2 mb-1">IDEAS</h4>
                          {searchResults.ideas.slice(0, 5).map(idea => (
                            <button
                              key={idea.id}
                              onClick={() => handleSearchResultClick(idea, 'idea')}
                              className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#1e2a45] transition-colors"
                            >
                              <p className="text-sm text-white truncate">{idea.title}</p>
                              <p className="text-xs text-gray-500">{idea.status} • {idea.votes} votes</p>
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {/* Categories */}
                      {searchResults.categories?.length > 0 && (
                        <div className="p-2 border-t border-[#1e2a45]">
                          <h4 className="text-xs font-semibold text-gray-500 px-2 mb-1">CATEGORIES</h4>
                          {searchResults.categories.map(cat => (
                            <button
                              key={cat.id}
                              onClick={() => handleSearchResultClick(cat, 'category')}
                              className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#1e2a45] transition-colors"
                            >
                              <p className="text-sm text-white">{cat.name}</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            {loading ? (
              <div className="w-8 h-8 rounded-full bg-[#1e2a45] animate-pulse" />
            ) : isAuthenticated ? (
              <>
                {/* Subscription Status */}
                {!isSubscribed && (
                  <div className="relative">
                    <Button
                      onClick={() => setShowPricingDropdown(!showPricingDropdown)}
                      data-testid="subscribe-btn"
                      className="text-sm font-semibold bg-gradient-to-b from-[#F97316] to-[#EA580C] hover:from-[#FB923C] hover:to-[#F97316] text-white rounded-full px-4 py-2 shadow-[0_4px_0_0_#C2410C,0_6px_15px_rgba(249,115,22,0.3)] hover:shadow-[0_2px_0_0_#C2410C,0_4px_12px_rgba(249,115,22,0.3)] hover:translate-y-[2px] active:shadow-[0_0px_0_0_#C2410C] active:translate-y-[4px] transition-all duration-100"
                    >
                      <CreditCard className="h-4 w-4 mr-1" />
                      Subscribe
                    </Button>
                    
                    {/* Pricing Dropdown */}
                    {showPricingDropdown && (
                      <div className="absolute right-0 mt-2 w-72 bg-[#131b2e] border border-[#1e2a45] rounded-xl shadow-2xl overflow-hidden z-50">
                        <div className="p-3 border-b border-[#1e2a45]">
                          <h4 className="text-white font-semibold">Choose a Plan</h4>
                        </div>
                        <div className="p-2 space-y-2">
                          <button
                            onClick={() => handleSubscribe('monthly')}
                            className="w-full flex justify-between items-center p-3 rounded-lg bg-[#1e2a45] hover:bg-[#2d3a56] transition-colors"
                          >
                            <span className="text-white font-medium">Monthly</span>
                            <span className="text-[#3B82F6] font-bold">$7.99/mo</span>
                          </button>
                          <button
                            onClick={() => handleSubscribe('semiannually')}
                            className="w-full flex justify-between items-center p-3 rounded-lg bg-[#1e2a45] hover:bg-[#2d3a56] transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium">Semi-Annually</span>
                              <span className="text-xs bg-[#8B5CF6]/20 text-[#8B5CF6] px-1.5 py-0.5 rounded">Save 4%</span>
                            </div>
                            <span className="text-[#8B5CF6] font-bold">$46.00/6mo</span>
                          </button>
                          <button
                            onClick={() => handleSubscribe('yearly')}
                            className="w-full flex justify-between items-center p-3 rounded-lg bg-gradient-to-r from-[#F97316]/10 to-[#EA580C]/10 border border-[#F97316]/30 hover:from-[#F97316]/20 hover:to-[#EA580C]/20 transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium">Yearly</span>
                              <span className="text-xs bg-[#F97316]/20 text-[#F97316] px-1.5 py-0.5 rounded">Best Value</span>
                            </div>
                            <span className="text-[#F97316] font-bold">$86.99/yr</span>
                          </button>
                        </div>
                        <div className="p-2 border-t border-[#1e2a45]">
                          <p className="text-xs text-gray-500 text-center">Auto-renews on anniversary date</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* User Menu */}
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    data-testid="user-menu-btn"
                    className="flex items-center space-x-2 px-3 py-2 rounded-full bg-[#131b2e] border border-[#1e2a45] hover:border-[#3B82F6] transition-colors"
                  >
                    {user?.picture ? (
                      <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                    ) : (
                      <User className="h-5 w-5 text-gray-400" />
                    )}
                    <span className="text-sm text-gray-300 max-w-[100px] truncate">{user?.name || user?.email}</span>
                    {isSubscribed && (
                      <span className="text-xs bg-[#3B82F6]/20 text-[#3B82F6] px-1.5 py-0.5 rounded">PRO</span>
                    )}
                  </button>
                  
                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-[#131b2e] border border-[#1e2a45] rounded-xl shadow-xl py-2 z-50">
                      <div className="px-4 py-2 border-b border-[#1e2a45]">
                        <p className="text-sm font-medium text-white truncate">{user?.username || user?.email}</p>
                        <p className="text-xs text-gray-500">{isSubscribed ? 'Pro Member' : 'Free Account'}</p>
                      </div>
                      <Link
                        to="/profile"
                        onClick={() => setUserMenuOpen(false)}
                        data-testid="profile-link"
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-[#1e2a45] transition-colors"
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        Profile Settings
                      </Link>
                      <Link
                        to="/dashboard"
                        onClick={() => setUserMenuOpen(false)}
                        data-testid="dashboard-link"
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-[#1e2a45] transition-colors"
                      >
                        <LayoutDashboard className="h-4 w-4 mr-2" />
                        Dashboard
                      </Link>
                      <Link
                        to="/trading"
                        onClick={() => setUserMenuOpen(false)}
                        data-testid="trading-history-link"
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-[#1e2a45] transition-colors"
                      >
                        <History className="h-4 w-4 mr-2" />
                        Trading History
                      </Link>
                      <Link
                        to="/watchlist"
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-[#1e2a45] transition-colors"
                      >
                        <Bookmark className="h-4 w-4 mr-2" />
                        My Watchlist
                      </Link>
                      <button
                        type="button"
                        onClick={() => { setUserMenuOpen(false); setNotificationDialogOpen(true); }}
                        data-testid="notifications-settings-menu-item"
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-[#1e2a45] transition-colors"
                      >
                        <Bell className="h-4 w-4 mr-2" />
                        Notifications
                      </button>
                      <Link
                        to="/ideas"
                        onClick={() => setUserMenuOpen(false)}
                        data-testid="created-markets-link"
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-[#1e2a45] transition-colors"
                      >
                        <Lightbulb className="h-4 w-4 mr-2" />
                        Created Markets
                      </Link>
                      <Link
                        to="/invitations"
                        onClick={() => setUserMenuOpen(false)}
                        data-testid="invitations-link"
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-[#1e2a45] transition-colors"
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        Invite Friends
                      </Link>
                      {user?.is_admin && (
                        <Link
                          to="/admin"
                          onClick={() => setUserMenuOpen(false)}
                          data-testid="admin-link"
                          className="w-full flex items-center px-4 py-2 text-sm text-[#F97316] hover:bg-[#1e2a45] transition-colors"
                        >
                          <Shield className="h-4 w-4 mr-2" />
                          Admin Panel
                        </Link>
                      )}
                      <button
                        onClick={() => {/* TODO: Navigate to wallet */}}
                        className="w-full flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-[#1e2a45] transition-colors"
                      >
                        <Wallet className="h-4 w-4 mr-2" />
                        Wallet
                      </button>
                      <button
                        onClick={() => { logout(); setUserMenuOpen(false); }}
                        data-testid="logout-btn"
                        className="w-full flex items-center px-4 py-2 text-sm text-red-400 hover:bg-[#1e2a45] transition-colors"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => openAuthModal('login')}
                  data-testid="login-btn"
                  className="text-sm font-medium text-gray-300 hover:text-[#3B82F6] hover:bg-transparent"
                >
                  Log in
                </Button>
                <Button 
                  onClick={() => openAuthModal('register')}
                  data-testid="signup-btn"
                  className="text-sm font-semibold bg-gradient-to-b from-[#3B82F6] to-[#1D4ED8] hover:from-[#2563EB] hover:to-[#1E40AF] text-white rounded-full px-6 py-2 shadow-[0_4px_0_0_#1E40AF,0_6px_15px_rgba(37,99,235,0.3)] hover:shadow-[0_2px_0_0_#1E40AF,0_4px_12px_rgba(37,99,235,0.3)] hover:translate-y-[2px] active:shadow-[0_0px_0_0_#1E40AF] active:translate-y-[4px] transition-all duration-100"
                >
                  Sign up
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-gray-300" />
            ) : (
              <Menu className="h-6 w-6 text-gray-300" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#1e2a45]">
            {/* Mobile Search */}
            <div className="relative mb-4">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                type="text"
                placeholder="Search markets or profiles"
                className="w-full pl-10 pr-4 py-2 bg-[#131b2e] border-[#1e2a45] rounded-full text-sm text-gray-200"
              />
            </div>
            
            <nav className="flex flex-col space-y-3">
              {isAuthenticated ? (
                <Link
                  to="/ideas"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    trackFunnelEvent('banner_click', { source: 'nav_markets_creation_mobile', userId: user?.user_id });
                  }}
                  data-testid="nav-markets-creation-mobile"
                  className="text-sm font-extrabold uppercase tracking-wide text-white bg-gradient-to-b from-[#3B82F6] to-[#1D4ED8] px-3 py-2 rounded-full shadow-[0_3px_0_0_#1E40AF] text-center"
                >
                  MARKETS CREATION
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    trackFunnelEvent('banner_click', { source: 'nav_markets_creation_mobile' });
                    trackFunnelEvent('auth_shown', { source: 'nav_markets_creation_mobile' });
                    openAuthModal('login');
                  }}
                  data-testid="nav-markets-creation-mobile"
                  className="text-sm font-extrabold uppercase tracking-wide text-white bg-gradient-to-b from-[#3B82F6] to-[#1D4ED8] px-3 py-2 rounded-full shadow-[0_3px_0_0_#1E40AF] text-center"
                >
                  MARKETS CREATION
                </button>
              )}
              <Link to="/" className="text-sm font-medium text-gray-300 py-2">
                Markets
              </Link>
              <Link to="/?category=right_now" className="text-sm font-medium text-[#F97316] py-2">
                Right Now
              </Link>
              <Link to="/scores" className="text-sm font-medium text-gray-300 py-2">
                Scores
              </Link>
              <Link to="/api" className="text-sm font-medium text-gray-300 py-2">
                API
              </Link>
            </nav>
            
            <div className="flex flex-col space-y-3 mt-4 pt-4 border-t border-[#1e2a45]">
              {isAuthenticated ? (
                <>
                  <div className="flex items-center space-x-2 px-2 py-2">
                    {user?.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <User className="h-6 w-6 text-gray-400" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-white">{user?.name || user?.email}</p>
                      <p className="text-xs text-gray-500">{isSubscribed ? 'Pro Member' : 'Free Account'}</p>
                    </div>
                  </div>
                  {!isSubscribed && (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-400 text-center">Choose a subscription plan:</p>
                      <Button
                        onClick={() => handleSubscribe('monthly')}
                        className="w-full text-sm font-semibold bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-full flex justify-between items-center px-4"
                      >
                        <span>Monthly</span>
                        <span>$7.99/mo</span>
                      </Button>
                      <Button
                        onClick={() => handleSubscribe('semiannually')}
                        className="w-full text-sm font-semibold bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded-full flex justify-between items-center px-4"
                      >
                        <span>Semi-Annually</span>
                        <span>$46.00/6mo</span>
                      </Button>
                      <Button
                        onClick={() => handleSubscribe('yearly')}
                        className="w-full text-sm font-semibold bg-gradient-to-b from-[#F97316] to-[#EA580C] text-white rounded-full flex justify-between items-center px-4"
                      >
                        <span>Yearly (Best Value)</span>
                        <span>$86.99/yr</span>
                      </Button>
                    </div>
                  )}
                  <Button
                    onClick={logout}
                    variant="ghost"
                    className="w-full text-sm text-red-400 hover:bg-[#1e2a45]"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign out
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => { openAuthModal('login'); setMobileMenuOpen(false); }}
                    className="w-full text-sm font-medium text-gray-300"
                  >
                    Log in
                  </Button>
                  <Button 
                    onClick={() => { openAuthModal('register'); setMobileMenuOpen(false); }}
                    className="w-full text-sm font-semibold bg-gradient-to-b from-[#3B82F6] to-[#1D4ED8] text-white rounded-full"
                  >
                    Sign up
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={authModalOpen} 
        onClose={() => setAuthModalOpen(false)}
        initialView={authModalView}
      />

      {/* Notification Settings */}
      <NotificationSettingsDialog
        open={notificationDialogOpen}
        onOpenChange={setNotificationDialogOpen}
      />
    </header>
  );
};

export default Header;
